import React, { useState, useEffect, useRef } from 'react';
import { getLogs, subscribeToLogs, clearLogs } from '../utils/logger';
import { CloseIcon, TrashIcon, CheckmarkIcon } from './Icons';
import { sendTestPush, fetchServerLogs } from '../api';
import { getCurrentToken } from '../utils/notificationUtils';

export const DebugLogViewer = ({ 
    isOpen, 
    onClose,
    onRefreshPush,
    currentUser
}: { 
    isOpen: boolean, 
    onClose: () => void,
    onRefreshPush?: () => void,
    currentUser?: any
}) => {
    const [viewMode, setViewMode] = useState<'client' | 'server'>('client');
    const [logs, setLogs] = useState(getLogs());
    const [serverLogs, setServerLogs] = useState<any[]>([]);
    const [filter, setFilter] = useState('');
    const [deviceToken, setDeviceToken] = useState<string | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            if (viewMode === 'client') {
                const unsubscribe = subscribeToLogs(setLogs);
                setLogs(getLogs());
                
                getCurrentToken().then(token => {
                    if (token) setDeviceToken(token);
                });

                return unsubscribe;
            } else {
                const fetchLogs = async () => {
                    try {
                        const data = await fetchServerLogs();
                        if (Array.isArray(data)) {
                            setServerLogs(data.reverse()); // Show latest first
                        } else {
                            console.warn('[System] Server logs returned non-array:', data);
                            setServerLogs([{ timestamp: new Date().toISOString(), level: 'WARN', message: 'Server returned non-array logs' }]);
                        }
                    } catch (e: any) {
                        console.error('Failed to fetch server logs:', e.message || e);
                    }
                };
                fetchLogs();
                const interval = setInterval(fetchLogs, 3000);
                return () => clearInterval(interval);
            }
        }
    }, [isOpen, viewMode]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0; // Latest logs are at top
        }
    }, [logs, serverLogs]);

    if (!isOpen) return null;

    const activeLogs = viewMode === 'client' ? logs : serverLogs;

    const filteredLogs = activeLogs.filter(log => {
        const msg = (log.message || '').toLowerCase();
        const lvl = (log.level || '').toLowerCase();
        const search = filter.toLowerCase();
        return msg.includes(search) || lvl.includes(search);
    });

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 md:p-8">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
            <div className="relative w-full max-w-2xl h-[80vh] bg-gray-900 border border-white/10 rounded-[2rem] flex flex-col overflow-hidden shadow-2xl">
                <div className="p-6 border-b border-white/5 flex items-center justify-between bg-black/20">
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                        <h3 className="font-black italic uppercase tracking-tighter text-white">System Console</h3>
                        <div className="flex bg-white/5 p-0.5 rounded-lg border border-white/10 ml-2">
                            <button 
                                onClick={() => setViewMode('client')}
                                className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase transition-all ${viewMode === 'client' ? 'bg-white/10 text-white shadow-lg' : 'text-white/30 hover:text-white/60'}`}
                            >
                                Client
                            </button>
                            <button 
                                onClick={() => setViewMode('server')}
                                className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase transition-all ${viewMode === 'server' ? 'bg-white/10 text-white shadow-lg' : 'text-white/30 hover:text-white/60'}`}
                            >
                                Server
                            </button>
                        </div>
                        <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded-full text-white/40">
                            {viewMode === 'client' ? logs.length : serverLogs.length} entries
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {onRefreshPush && (
                            <button 
                                onClick={onRefreshPush} 
                                className="p-2 hover:bg-white/10 rounded-full text-blue-400 hover:text-blue-300 transition-all flex items-center gap-1 px-3"
                                title="Retry Push Setup"
                            >
                                <span className="text-[10px] font-bold uppercase">Setup Push</span>
                            </button>
                        )}
                        {currentUser && (
                            <button 
                                onClick={async () => {
                                    const username = typeof currentUser === 'string' ? currentUser : currentUser.username;
                                    console.log(`[Push] Requesting test notification for ${username}...`);
                                    try {
                                        const res = await sendTestPush(username);
                                        console.log('[Push] Server Response:', JSON.stringify(res, null, 2));
                                    } catch (e: any) {
                                        console.error('[Push] Test push request failed:', e.message || e);
                                    }
                                }} 
                                className="p-2 hover:bg-white/10 rounded-full text-green-400 hover:text-green-300 transition-all flex items-center gap-1 px-3"
                                title="Send Test Push"
                            >
                                <span className="text-[10px] font-bold uppercase">Test Push</span>
                            </button>
                        )}
                        <button onClick={clearLogs} className="p-2 hover:bg-white/10 rounded-full text-white/40 hover:text-red-400 transition-all">
                            <TrashIcon className="w-5 h-5" />
                        </button>
                        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-white">
                            <CloseIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="p-4 border-b border-white/5 bg-black/10">
                    <input 
                        type="text" 
                        placeholder="Filter logs (e.g. [Push], Error, token)..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-xs text-white outline-none focus:border-blue-500 transition-all"
                    />
                    {deviceToken && (
                        <div className="mt-2 flex items-center justify-between text-[9px] text-white/30 uppercase tracking-widest font-mono">
                            <span className="truncate mr-2">Device Token: {deviceToken.substring(0, 15)}...{deviceToken.substring(deviceToken.length - 10)}</span>
                            <button 
                                onClick={() => {
                                    navigator.clipboard.writeText(deviceToken);
                                    console.log('[System] Token copied to clipboard');
                                }}
                                className="hover:text-white transition-colors flex-shrink-0"
                            >
                                Copy Token
                            </button>
                        </div>
                    )}
                </div>

                <div 
                    ref={scrollRef} 
                    className="flex-1 overflow-y-auto p-4 space-y-1 font-mono text-[10px] no-scrollbar"
                    style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    {filteredLogs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full opacity-20 italic">
                            <p>No {viewMode} logs matching filter</p>
                        </div>
                    ) : (
                        filteredLogs.map((log, i) => (
                            <div key={i} className={`p-2 rounded border border-white/5 break-words whitespace-pre-wrap ${
                                log.level.toLowerCase() === 'error' ? 'bg-red-500/10 border-red-500/20 text-red-400' :
                                log.level.toLowerCase() === 'warn' ? 'bg-orange-500/10 border-orange-500/20 text-orange-400' :
                                'text-gray-300'
                            }`}>
                                <div className="flex items-center justify-between mb-1 opacity-40">
                                    <span className="uppercase font-black tracking-widest">{log.level}</span>
                                    <span>{log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : ''}</span>
                                </div>
                                <div className="font-medium">{log.message}</div>
                            </div>
                        ))
                    )}
                </div>
                
                <div className="p-4 bg-black/40 text-[9px] text-white/20 text-center uppercase tracking-widest font-black italic">
                    Internal Debug Engine v1.0
                </div>
            </div>
        </div>
    );
};
